/* ==========================================================================
   Addresses for the app's screens

   The whole app used to live at one address. Refreshing threw you back to
   the first tab, nothing inside could be linked to or bookmarked, a new tab
   could not open a particular screen, and a notification could never say
   "open this chat". Every screen now has an address -- /work/abc, /chats/xyz,
   /requests -- that the address bar shows and that opens the same screen
   again later.

   Two directions:
     pathFor(view, params)    the address to show for what is on screen
     resolvePath(path, role)  what to open for an address someone arrived at

   Chats and Settings are the same address for both sides, so an address means
   "this place, for whoever you are". The public pages (/how-it-works/, /homes/,
   /cleaners/, /es/, /faq/, /areas/...) are separate files the server hands out
   before the app is involved; none of these addresses collide with them.
   ========================================================================== */

import type { Role } from '../types';
import type { ScreenName } from '../store';

type Params = { id?: string };

/* view -> address. ':id' is filled from params.id. */
const PATH: Record<ScreenName, string> = {
  // a home
  h_find: '/find',
  h_cleaner: '/find/:id',
  h_post: '/post',
  h_requests: '/requests',
  h_request: '/requests/:id',
  h_editrequest: '/requests/:id/edit',
  h_pastjob: '/requests/log-past-job',
  h_booking: '/bookings/:id',
  h_joblist: '/home/job-list',
  h_messages: '/chats',
  h_chat: '/chats/:id',
  h_profile: '/home',
  h_edithome: '/home/edit',
  // a cleaner
  c_jobs: '/work',
  c_job: '/work/:id',
  c_calendar: '/calendar',
  c_editweek: '/calendar/week',
  c_bookings: '/my-jobs',
  c_booking: '/my-jobs/:id',
  c_inprogress: '/my-jobs/:id/now',
  c_messages: '/chats',
  c_chat: '/chats/:id',
  c_profile: '/profile',
  c_editprofile: '/profile/edit',
  // either
  notifications: '/updates',
  review: '/review/:id',
  feedback: '/feedback',
  settings: '/settings',
  admin: '/admin',
  sql: '/admin/sql',
};

export function pathFor(view: ScreenName, params: Params = {}): string {
  const p = PATH[view];
  if (!p) return '/';
  return p.includes(':id') ? (params.id ? p.replace(':id', encodeURIComponent(params.id)) : '/') : p;
}

/* Which views each side may arrive at by address. A cleaner opening /find is
   a home's screen; they get their own first tab instead. */
const HOUSE_VIEWS: ScreenName[] = ['h_find', 'h_cleaner', 'h_post', 'h_requests', 'h_request', 'h_editrequest',
  'h_pastjob', 'h_booking', 'h_joblist', 'h_messages', 'h_chat', 'h_profile', 'h_edithome'];
const CLEANER_VIEWS: ScreenName[] = ['c_jobs', 'c_job', 'c_calendar', 'c_editweek', 'c_bookings', 'c_booking',
  'c_inprogress', 'c_messages', 'c_chat', 'c_profile', 'c_editprofile'];
const SHARED_VIEWS: ScreenName[] = ['notifications', 'review', 'feedback', 'settings', 'admin', 'sql'];

/* Screens that are a form or a moment, not a place. Arriving at one by
   address (a refresh mid-edit, an old tab) opens the thing it was about
   instead: there is no half-filled form to come back to. */
const ARRIVE_AS: Partial<Record<ScreenName, ScreenName>> = {
  h_editrequest: 'h_request',
  h_pastjob: 'h_requests',
  h_edithome: 'h_profile',
  c_editprofile: 'c_profile',
  c_editweek: 'c_calendar',
  c_inprogress: 'c_booking',
};

/* The list each screen belongs to, so an address opened cold still has
   somewhere for Back to go. */
export const PARENT: Partial<Record<ScreenName, ScreenName>> = {
  h_cleaner: 'h_find', h_request: 'h_requests', h_booking: 'h_requests', h_joblist: 'h_profile',
  h_chat: 'h_messages', c_job: 'c_jobs', c_booking: 'c_bookings', c_chat: 'c_messages',
};

export interface Arrival { view: ScreenName; params: Params }

/** What to open for an address, for this side -- or null when the address is
 *  not an app screen (or not theirs). `exists` checks an id still points at
 *  something this person can see; a job that has been filled, a chat that is
 *  gone, falls back to the list it lived in. */
export function resolvePath(
  path: string, role: Role, exists: (view: ScreenName, id: string) => boolean, isAdmin: boolean,
): Arrival | null {
  const clean = (path.replace(/\/+$/, '') || '/');
  if (clean === '/') return null;
  const allowed = [...(role === 'house' ? HOUSE_VIEWS : CLEANER_VIEWS), ...SHARED_VIEWS];
  for (const view of allowed) {
    const pattern = PATH[view];
    if (!pattern) continue;
    const rx = new RegExp('^' + pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(':id', '([^/]+)') + '$');
    const m = rx.exec(clean);
    if (!m) continue;
    if ((view === 'admin' || view === 'sql') && !isAdmin) return null;
    const id = m[1] ? decodeURIComponent(m[1]) : undefined;
    /* A review is written from a booking; arriving at one by address opens
       the booking, where the button to write it is. */
    let v = view === 'review' ? (role === 'house' ? 'h_booking' : 'c_booking') : (ARRIVE_AS[view] ?? view);
    if (id && !exists(v, id)) {
      v = PARENT[v] ?? v;
      return v === view || PATH[v].includes(':id') ? null : { view: v, params: {} };
    }
    return { view: v, params: id ? { id } : {} };
  }
  return null;
}
